import { io } from 'socket.io-client';
import { isJwtExpired } from './utils/jwt'; // adjust path as needed

class SocketConnection {
  socketEndpointOrders = 'https://api-v2.pesabase.com/orders';
  socketEndpointNoti = 'https://api.pesabase.com/notifications';
  socketEndpointVerification = 'https://api-v2.pesabase.com/verification';

  // The constructor will initialize the Socket Connection
  constructor(jwt, agent) {
    if (agent) {
      this.socketOrders = io(this.socketEndpointOrders, {
        autoConnect: false,
        extraHeaders: {
          token: `${jwt}`,
        },
        transports: ['polling', 'websocket'],
      });
    }
    
    this.socketNotifications = io(this.socketEndpointNoti, {
      autoConnect: false,
      extraHeaders: {
        token: `${jwt}`,
      },
      transports: ['polling', 'websocket'],
    });

    // Verification socket connection whatsapp
    this.socketVerification = io(this.socketEndpointVerification, {
      autoConnect: false,
      extraHeaders: {
        token: `${jwt}`,
      },
      transports: ['polling', 'websocket'],
    });
  }

  // Method to connect all sockets
  connect() {
    // Check token before connecting
    const token =
      this.socketOrders?.io?.opts?.extraHeaders?.token ||
      this.socketNotifications?.io?.opts?.extraHeaders?.token ||
      this.socketVerification?.io?.opts?.extraHeaders?.token;

    if (isJwtExpired(token)) {
      console.warn('JWT is expired. Sockets will not connect.');
      return;
    }

    if (this.socketOrders) {
      this.socketOrders.connect();
    }
    if (this.socketNotifications) {
      this.socketNotifications.connect();
    }
    if (this.socketVerification) {
      this.socketVerification.connect();
    }
  }

  // Method to disconnect all sockets
  disconnect() {
    if (this.socketOrders) {
      this.socketOrders.disconnect();
    }
    if (this.socketNotifications) {
      this.socketNotifications.disconnect();
    }
    if (this.socketVerification) {
      this.socketVerification.disconnect();
    }
  }

  // Method to check connection status of all sockets
  getConnectionStatus() {
    const status = {
      orders: null,
      notifications: null,
      verification: null,
      timestamp: new Date().toISOString(),
      summary: {
        total: 0,
        connected: 0,
        disconnected: 0,
        error: 0
      }
    };

    // Check orders socket (only for agents)
    if (this.socketOrders) {
      status.orders = {
        exists: true,
        connected: this.socketOrders.connected,
        id: this.socketOrders.id,
        transport: this.socketOrders.io?.engine?.transport?.name || 'unknown',
        url: this.socketEndpointOrders,
        events: 'available', // Simplified - we know events are available if socket exists
        auth: {
          hasToken: !!this.socketOrders.io?.opts?.extraHeaders?.token,
          tokenLength: this.socketOrders.io?.opts?.extraHeaders?.token?.length || 0
        }
      };
      status.summary.total++;
      if (this.socketOrders.connected) {
        status.summary.connected++;
      } else {
        status.summary.disconnected++;
      }
    } else {
      status.orders = {
        exists: false,
        reason: 'User is not an agent'
      };
    }

    // Check notifications socket
    if (this.socketNotifications) {
      status.notifications = {
        exists: true,
        connected: this.socketNotifications.connected,
        id: this.socketNotifications.id,
        transport: this.socketNotifications.io?.engine?.transport?.name || 'unknown',
        url: this.socketEndpointNoti,
        events: 'available', // Simplified - we know events are available if socket exists
        auth: {
          hasToken: !!this.socketNotifications.io?.opts?.extraHeaders?.token,
          tokenLength: this.socketNotifications.io?.opts?.extraHeaders?.token?.length || 0
        }
      };
      status.summary.total++;
      if (this.socketNotifications.connected) {
        status.summary.connected++;
      } else {
        status.summary.disconnected++;
      }
    }

    // Check verification socket
    if (this.socketVerification) {
      status.verification = {
        exists: true,
        connected: this.socketVerification.connected,
        id: this.socketVerification.id,
        transport: this.socketVerification.io?.engine?.transport?.name || 'unknown',
        url: this.socketEndpointVerification,
        events: 'available', // Simplified - we know events are available if socket exists
        auth: {
          hasToken: !!this.socketVerification.io?.opts?.extraHeaders?.token,
          tokenLength: this.socketVerification.io?.opts?.extraHeaders?.token?.length || 0
        }
      };
      status.summary.total++;
      if (this.socketVerification.connected) {
        status.summary.connected++;
      } else {
        status.summary.disconnected++;
      }
    }

    return status;
  }

  // Method to get detailed health check
  getHealthCheck() {
    const status = this.getConnectionStatus();
    const health = {
      overall: 'unknown',
      issues: [],
      recommendations: [],
      details: status
    };

    // Check for connection issues
    if (status.orders?.exists && !status.orders.connected) {
      health.issues.push('Orders socket is not connected');
      health.recommendations.push('Check network connection and JWT token');
    }

    if (status.notifications?.exists && !status.notifications.connected) {
      health.issues.push('Notifications socket is not connected');
      health.recommendations.push('Check network connection and JWT token');
    }

    if (status.verification?.exists && !status.verification.connected) {
      health.issues.push('Verification socket is not connected');
      health.recommendations.push('Check network connection and JWT token');
    }

    // Check authentication
    [status.orders, status.notifications, status.verification].forEach(socket => {
      if (socket?.exists && !socket.auth.hasToken) {
        health.issues.push(`${socket.url} socket has no authentication token`);
        health.recommendations.push('Ensure JWT token is provided');
      }
    });

    // Determine overall health
    if (health.issues.length === 0) {
      health.overall = 'healthy';
    } else if (health.issues.length <= 1) {
      health.overall = 'warning';
    } else {
      health.overall = 'critical';
    }

    return health;
  }

  // Method to force reconnect all sockets
  forceReconnect() {
    console.log('Force reconnecting all sockets...');
    
    if (this.socketOrders) {
      this.socketOrders.disconnect();
      setTimeout(() => this.socketOrders.connect(), 1000);
    }
    
    if (this.socketNotifications) {
      this.socketNotifications.disconnect();
      setTimeout(() => this.socketNotifications.connect(), 1000);
    }
    
    if (this.socketVerification) {
      this.socketVerification.disconnect();
      setTimeout(() => this.socketVerification.connect(), 1000);
    }
  }
}

let socketConnection;

// The SocketFactory is responsible for creating and returning a single instance of the SocketConnection class
// Implementing the singleton pattern
class SocketFactory {
  static create(jwt, agent) {
    // Debug logging for undefined agent parameter
    if (agent === undefined) {
      console.warn('⚠️ SocketFactory.create called with undefined agent parameter');
      console.warn('JWT exists:', !!jwt);
      console.warn('Stack trace:', new Error().stack);
      // Default to false (user) if agent is undefined
      agent = false;
    }
    
    // Check if we need to recreate the connection due to role change
    if (socketConnection) {
      const currentAgent = socketConnection.socketOrders !== null;
      if (currentAgent !== agent) {
        console.log('Role changed, recreating socket connection');
        console.log('Previous role (agent):', currentAgent, 'New role (agent):', agent);
        socketConnection.disconnect();
        socketConnection = null;
      }
    }
    
    if (!socketConnection) {
      console.log('Creating new socket connection, agent:', agent);
      socketConnection = new SocketConnection(jwt, agent);
    }
    return socketConnection;
  }

  // Method to reset the singleton instance (useful for testing or reconnection)
  static reset() {
    if (socketConnection) {
      socketConnection.disconnect();
      socketConnection = null;
    }
  }

  // Method to check all socket connections
  static checkAllConnections() {
    if (!socketConnection) {
      return {
        error: 'No socket connection exists',
        timestamp: new Date().toISOString()
      };
    }
    return socketConnection.getConnectionStatus();
  }

  // Method to get health check
  static getHealthCheck() {
    if (!socketConnection) {
      return {
        overall: 'error',
        issues: ['No socket connection exists'],
        recommendations: ['Initialize socket connection first'],
        timestamp: new Date().toISOString()
      };
    }
    return socketConnection.getHealthCheck();
  }

  // Method to force reconnect
  static forceReconnect() {
    if (socketConnection) {
      socketConnection.forceReconnect();
      return { message: 'Force reconnection initiated' };
    }
    return { error: 'No socket connection exists' };
  }

  // Method to get current socket instance
  static getCurrentConnection() {
    return socketConnection;
  }

  // Method to update connection with new role
  static updateRole(jwt, newAgent) {
    console.log('Updating socket connection for role change to agent:', newAgent);
    return this.create(jwt, newAgent);
  }
}

export default SocketFactory;
